import type { CanonicalColumnId } from '@features/event-level-column-rename/types';

/**
 * Supplier quote grid (`initialColumnSettings.tsx`) column key to canonical id.
 *
 * Local to this surface (POLARIS-3115) — the frozen buyer table's
 * `BUYER_GRID_COLUMN_CANONICAL_IDS` is not reused across surfaces. Each entry is proven from
 * `initialColumnSettings.tsx`'s own code, cross-referenced against CONTRACT §1's canonical id
 * table:
 *
 * - `name` (`li.name`): `columnNameOverrides['general.line_item_name']` — CONTRACT §1's Rails/client key for `lineItemName`.
 * - `uom` (`li.units`): `columnNameOverrides['general.uom']` — Rails/client key for `unitOfMeasure`.
 * - `price` (`quote.price`): `columnNameOverrides['general.price_per_unit']` — Rails/client key for `pricePerUnit`.
 * - `suggestedPricePerUnit` (`quote.suggested_price_per_unit`): `columnNameOverrides['line_items.suggested_price_per_unit']` — Rails/client key for `suggestedPricePerUnit`.
 * - `extendedQuantity` (`li.extended_quantity`): `columnNameOverrides['bid_request.extended_quantity']` — Rails key for `quantityOfItems`.
 * - `quantity` (`li.quantity`): `columnNameOverrides['bid_request.quantity']` — Rails key for `itemsPerUnit`.
 * - `quantityOfUnits` (`li.quantity_of_units`): `columnNameOverrides['general.quantity_of_units']` — Rails key for `quantityOfUnits`.
 * - `discount` (`quote.discount`): `columnNameOverrides['general.discount_per_unit']` — Rails/client key for `discountPerUnit`.
 * - `total` (`quote.total`): `columnNameOverrides['line_items.line_item_price']` — Rails/client key for `lineItemPrice`.
 * - `supplierQuantityOfUnits` (`quote.supplier_quantity`, BOTH branches): the
 *   `!collectItemsPerUnit` branch's default `t('line_items.quoted_quantity_of_units')` matches the
 *   client i18n key for `quotedQuantityOfUnits` exactly (event-only column per CONTRACT §1, no org
 *   override exists); the `collectItemsPerUnit` branch's "Quoted Quantity of Items" is the SAME
 *   canonical column wearing a conditional default in the IPU layout (AZ ruling, DECISIONS.md D28 —
 *   the D26 Extended/TLIP pattern), routed with its own default as the fallback.
 * - `totalAdditionalFees` (`quote.additional_fees`): default `t('bid_request.additional_fees')` —
 *   matches the client i18n key for `totalAdditionalCosts` exactly (event only, no org override).
 * - `totalAllInPrice` (`quote.all_in_price`): default `t('bid_request.total_all_in_price')` —
 *   matches the client i18n key for `totalAllInPrice` exactly (event only, no org override).
 *
 * Not mapped, and deliberately so: `quote.awarded`/`awarded_quantity`/`awarded_price` (`general.status`
 * etc. — no canonical id) and `general.surcharge` (not in CONTRACT §1).
 *
 * **`quote.supplier_quantity` (SSQ) note (POLARIS-3135 STATE.md Q12: "`quotedQuantityOfUnits`
 * aliases are folded into the contract's alias table" — i.e. `line_items.quoted_quantity_of_units`
 * is the *only* valid spelling; no other key is an alias for it).** BOTH branches of this grid's
 * SSQ column route through `quotedQuantityOfUnits`: the `!collectItemsPerUnit` branch's default is
 * that exact key; the `collectItemsPerUnit` branch's "Quoted Quantity of Items" is the same
 * canonical column's conditional default in the IPU layout (AZ ruling, DECISIONS.md D28 — the D26
 * Extended/TLIP pattern; per-branch defaults preserved as fallbacks). The legacy twin grids
 * (`Lot.jsx`/`LotAutosave.jsx`) are deprecated and receive no rename wiring at all (DECISIONS.md
 * D23/D25 — the legacy wiring drafted in PR #4382 was declined and closed).
 * `line_items.quoted_quantity` and `line_items.supplier_quantity` are **not** aliases —
 * DECISIONS.md C2 confirms both were fabricated during the spike and exist as translations in
 * neither repo.
 *
 * A guessed entry compiles, passes its own tests and renames the wrong column — do not extend this
 * by inference; add only what this grid's own code proves.
 */
export const SUPPLIER_GRID_COLUMN_CANONICAL_IDS = {
  discount: 'discountPerUnit',
  extendedQuantity: 'quantityOfItems',
  name: 'lineItemName',
  price: 'pricePerUnit',
  quantity: 'itemsPerUnit',
  quantityOfUnits: 'quantityOfUnits',
  suggestedPricePerUnit: 'suggestedPricePerUnit',
  supplierQuantityOfUnits: 'quotedQuantityOfUnits',
  total: 'lineItemPrice',
  totalAdditionalFees: 'totalAdditionalCosts',
  totalAllInPrice: 'totalAllInPrice',
  uom: 'unitOfMeasure',
} as const satisfies Record<string, CanonicalColumnId>;
